import {
  Box,
  VStack,
  Text,
  Center,
  Flex,
  Button,
} from '@wegroup/design-system';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce/lib';
import LoadingSpinner from '../../../../../../common/src/components/LoadingSpinner';
import { useNaceCodesQuery } from '../../../../hooks/useFlowsQueries';
import { NaceCode, NaceCodeList } from '../../../../types/CompanyScanTypes';
import { FormValues } from './NaceCodeModal';
import NaceCodeTreeItem, { NACE_TYPE_LEVEL_MAPPING } from './NaceCodeTreeItem';

interface Props {
  naceCodes: NaceCode[];
  onAdd: (naceCode: NaceCodeList) => void;
  onRemove: (naceCode: NaceCodeList) => void;
  watch: UseFormWatch<FormValues>;
}

const NaceCodeTree: React.FC<React.PropsWithChildren<Props>> = ({
  naceCodes,
  onAdd,
  onRemove,
  watch,
}) => {
  const { t } = useTranslation();

  const searchQuery = watch('searchQuery');

  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const { data, isLoading } = useNaceCodesQuery(debouncedSearchQuery);

  const [isOpenMapping, setIsOpenMapping] = useState({});

  const getFullOpenMappingObject = useCallback(
    (data?: NaceCode[]) => {
      if (data?.length === 0) return {};

      let obj: Record<string, boolean> = {};

      data?.forEach((naceCode) => {
        const children: NaceCode[] =
          naceCode[NACE_TYPE_LEVEL_MAPPING[naceCode.level]];

        // Recursively get children object
        const childObj = getFullOpenMappingObject(children);

        // Merge the objects
        obj = {
          ...childObj,
          ...obj,
        };

        // Set the value for this entry
        obj[naceCode.code] = true;
      });

      return obj;
    },
    [data],
  );

  const handleOnOpenNaceCode = (code: string) => {
    setIsOpenMapping((obj) => ({ ...obj, [code]: true }));
  };

  const handleOnCloseNaceCode = (code: string) => {
    setIsOpenMapping((obj) => ({ ...obj, [code]: false }));
  };

  useEffect(() => {
    if (data && data.length > 0 && searchQuery.length > 1) {
      setIsOpenMapping(getFullOpenMappingObject(data));
    } else {
      setIsOpenMapping({});
    }
  }, [data]);

  if (isLoading)
    return (
      <Center p="6">
        <LoadingSpinner />
      </Center>
    );

  if (data?.length === 0)
    return (
      <Center p="6" pt="0">
        <Text textStyle="h4Headline">{t('No results found')}</Text>
      </Center>
    );

  return (
    <Box>
      <Flex justify="flex-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsOpenMapping({})}
          isDisabled={isEmpty(isOpenMapping)}
        >
          {t('COLLAPSE_ALL')}
        </Button>
      </Flex>

      {data?.map((naceCode) => {
        return (
          <VStack key={naceCode.code} spacing="4" align="stretch">
            <NaceCodeTreeItem
              selectedNaceCodes={naceCodes}
              isParentOpen={true}
              naceCode={naceCode}
              onSelected={onAdd}
              onDeselected={onRemove}
              isOpenMapping={isOpenMapping}
              onOpen={handleOnOpenNaceCode}
              onClose={handleOnCloseNaceCode}
            />
          </VStack>
        );
      })}
    </Box>
  );
};

export default NaceCodeTree;
